import {
  LP_SETTLEMENT_REPORT,
  useObservable,
  useSubscription,
  useWSFilterPipe,
  type MinimalSubscriptionResponse,
  type WebsocketRequest,
} from '@talos/kyoko';
import { isEqual } from 'lodash-es';
import { useCallback, useEffect, useState } from 'react';
import { map } from 'rxjs';
import type { LPSettlementReport } from '../types';
import { lpSettlementReportBlotterRow, type LPSettlementReportBlotterRow } from './blotter/lpSettlementReportRow';

interface UnsettledTransactionsBlotterServerFilter {}

interface LPSettlementReportRequest extends WebsocketRequest {
  BatchIDs?: string[];
}

interface TradeSettleReportRequest extends WebsocketRequest, UnsettledTransactionsBlotterServerFilter {}

function getUniqueKey(row: LPSettlementReport) {
  return row.EventID;
}

export const useUnsettledTransactionsBlotterObs = (batchID: string | undefined) => {
  const [tradeSettleReportRequest, setTradeSettleReportRequest] = useState<TradeSettleReportRequest | null>(null);

  useEffect(() => {
    setTradeSettleReportRequest(currentRequest => {
      const maybeNewRequest: LPSettlementReportRequest = {
        name: LP_SETTLEMENT_REPORT,
        tag: 'LP settlement report',
        sort_by: '-TransactTime',
        ...(batchID != null && { BatchIDs: [batchID] }),
      };

      return isEqual(currentRequest, maybeNewRequest) ? currentRequest : maybeNewRequest;
    });
  }, [batchID]);

  const { data: dataObservable, error: errorObservable } = useSubscription<LPSettlementReport>(
    tradeSettleReportRequest,
    {
      loadAll: false,
    }
  );

  const filterFunc = useCallback(
    (item: LPSettlementReport) => {
      // Filter out Trades with assigned BatchID in Unsettled Transactions view only
      if (!batchID && item.SettleBatchID) {
        return false;
      }

      return true;
    },
    [batchID]
  );

  const filterPipe = useWSFilterPipe({ getUniqueKey, filterFunc });

  const lpSettlementReportsRowObservable = useObservable<
    MinimalSubscriptionResponse<LPSettlementReportBlotterRow>
  >(() => {
    return dataObservable.pipe(
      filterPipe,
      map(message => ({ ...message, data: message.data.flatMap(lpSettlementReportBlotterRow) }))
    );
  }, [dataObservable, filterPipe]);

  return { dataObservable, errorObservable, lpSettlementReportsRowObservable };
};
